import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Calendar,
  CheckSquare,
  XSquare,
  AlertTriangle,
  Target,
  TrendingUp,
  Clock,
  Star,
  Lightbulb,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  Award,
  Smile,
  Frown,
  Meh,
  ThumbsUp,
  Heart
} from 'lucide-react';

const DaySummaryForm = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [summaryType, setSummaryType] = useState(''); // 'day', 'week', 'month', 'quarter', 'year'
  const [selectedDate, setSelectedDate] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [generalFeeling, setGeneralFeeling] = useState(3); // 1-5 scale
  
  // שאלות הסיכום
  const [summary, setSummary] = useState({
    goalsAchieved: '',
    tasksCompleted: '',
    unexpectedObstacles: '',
    obstacleHandling: '',
    whatWorkedWell: '',
    lessonsLearned: '',
    areasToImprove: '',
    keyHighlights: '',
    teamCollaboration: '',
    personalGrowth: '',
    nextPeriodGoals: ''
  });
  
  // משימות לסיכום יומי
  const [dailyPlans, setDailyPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [incompleteTasks, setIncompleteTasks] = useState([]);
  const [taskExplanations, setTaskExplanations] = useState({});

  // טעינת תכנונים יומיים
  useEffect(() => {
    if (summaryType === 'day') {
      const plans = JSON.parse(localStorage.getItem('dailyPlans') || '[]');
      const userPlans = plans.filter(plan => plan.userId === currentUser?.id);
      setDailyPlans(userPlans);
    }
  }, [currentUser?.id, summaryType]);

  // טעינת תכנון יומי נבחר
  useEffect(() => {
    if (summaryType === 'day' && selectedDate) {
      const plan = dailyPlans.find(p => p.date === selectedDate);
      if (plan) {
        setSelectedPlan(plan);
        const allTasks = [];
        Object.values(plan.schedule || {}).forEach(slot => {
          if (slot?.task) {
            allTasks.push(slot.task);
          }
        });
        setCompletedTasks([]);
        setIncompleteTasks([...allTasks]);
      } else {
        setSelectedPlan({
          summaryData: { date: selectedDate },
          schedule: {},
          selectedTasks: []
        });
        setCompletedTasks([]);
        setIncompleteTasks([]);
      }
    }
  }, [selectedDate, dailyPlans, summaryType]);

  const handleTaskStatusChange = (task, isCompleted) => {
    if (isCompleted) {
      setCompletedTasks(prev => [...prev, task]);
      setIncompleteTasks(prev => prev.filter(t => t.id !== task.id));
    } else {
      setIncompleteTasks(prev => [...prev, task]);
      setCompletedTasks(prev => prev.filter(t => t.id !== task.id));
    }
  };

  const handleSave = () => {
    const summaryData = {
      id: Date.now().toString(),
      userId: currentUser?.id || currentUser?.uid,
      userName: currentUser?.name,
      userRole: currentUser?.role,
      departmentId: currentUser?.departmentId,
      departmentName: currentUser?.departmentName,
      teamId: currentUser?.teamId,
      teamName: currentUser?.teamName,
      type: `${summaryType}-summary`,
      category: 'summary',
      summaryType,
      date: selectedDate,
      dateRange,
      generalFeeling,
      summary,
      createdAt: new Date().toISOString()
    };

    // הוספת נתונים ספציפיים לסיכום יומי
    if (summaryType === 'day') {
      summaryData.completedTasks = completedTasks;
      summaryData.incompleteTasks = incompleteTasks;
      summaryData.taskExplanations = taskExplanations;
    }

    // שמירה ב-localStorage
    const storageKey = summaryType === 'day' ? 'daySummaries' : 'periodSummaries';
    const summaries = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    const existingIndex = summaries.findIndex(s => {
      if (summaryType === 'day') {
        return s.date === selectedDate && s.userId === currentUser?.id;
      } else {
        return s.summaryType === summaryType && 
               s.dateRange?.start === dateRange.start && 
               s.dateRange?.end === dateRange.end && 
               s.userId === currentUser?.id;
      }
    });
    
    if (existingIndex >= 0) {
      summaries[existingIndex] = summaryData;
    } else {
      summaries.push(summaryData);
    }
    
    localStorage.setItem(storageKey, JSON.stringify(summaries));
    alert('הסיכום נשמר בהצלחה!');
    navigate('/dashboard');
  };

  const getDateRangeForPeriod = (type) => {
    const today = new Date();
    let start, end;
    
    switch(type) {
      case 'week':
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        start = today;
        end = today;
    }
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  const getSummaryTypeLabel = () => {
    switch(summaryType) {
      case 'day': return 'היום';
      case 'week': return 'השבוע';
      case 'month': return 'החודש';
      case 'quarter': return 'הרבעון';
      case 'year': return 'השנה';
      default: return '';
    }
  };

  const getFeelingEmoji = (value) => {
    switch(value) {
      case 1: return '😔';
      case 2: return '😕';
      case 3: return '😐';
      case 4: return '😊';
      case 5: return '😄';
      default: return '😐';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">סיכום תקופתי</h1>
                <p className="text-gray-600">סכם את התקופה שעברה והכן תוכנית לעתיד</p>
              </div>
            </div>
          </div>
        </div>

        {/* בחירת סוג סיכום */}
        {!summaryType && (
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
              מה תרצה לסכם?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <button
                onClick={() => {
                  setSummaryType('day');
                  setSelectedDate(new Date().toISOString().split('T')[0]);
                }}
                className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:shadow-lg transition-all duration-300 border border-blue-200"
              >
                <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">את היום שהיה</h3>
              </button>
              
              <button
                onClick={() => {
                  setSummaryType('week');
                  getDateRangeForPeriod('week');
                }}
                className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl hover:shadow-lg transition-all duration-300 border border-green-200"
              >
                <CalendarDays className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">את השבוע שהיה</h3>
              </button>
              
              <button
                onClick={() => {
                  setSummaryType('month');
                  getDateRangeForPeriod('month');
                }}
                className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:shadow-lg transition-all duration-300 border border-purple-200"
              >
                <CalendarRange className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">את החודש שהיה</h3>
              </button>
              
              <button
                onClick={() => {
                  setSummaryType('quarter');
                  getDateRangeForPeriod('quarter');
                }}
                className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl hover:shadow-lg transition-all duration-300 border border-orange-200"
              >
                <CalendarClock className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">את הרבעון שהיה</h3>
              </button>
              
              <button
                onClick={() => {
                  setSummaryType('year');
                  getDateRangeForPeriod('year');
                }}
                className="p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-xl hover:shadow-lg transition-all duration-300 border border-red-200"
              >
                <Award className="w-8 h-8 text-red-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">את השנה שהייתה</h3>
              </button>
            </div>
          </div>
        )}

        {/* תוכן הסיכום */}
        {summaryType && (
          <>
            {/* תאריכים נבחרים */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                סיכום {getSummaryTypeLabel()}
              </h3>
              {summaryType === 'day' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    בחר תאריך:
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      מתאריך:
                    </label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      עד תאריך:
                    </label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* משימות יומיות - רק לסיכום יומי */}
            {summaryType === 'day' && selectedDate && selectedPlan && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <CheckSquare className="w-5 h-5 text-green-500" />
                    <span>משימות שהושלמו</span>
                  </h3>
                  <div className="space-y-3">
                    {completedTasks.length > 0 ? (
                      completedTasks.map((task, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl">
                          <button
                            onClick={() => handleTaskStatusChange(task, false)}
                            className="w-6 h-6 bg-green-500 rounded flex items-center justify-center text-white hover:bg-green-600 transition-colors"
                          >
                            ✓
                          </button>
                          <div className="flex-1 text-right">
                            <div className="font-medium text-gray-900">{task.title}</div>
                            <div className="text-sm text-gray-600">{task.description}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-4">
                        אין משימות שהושלמו
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <XSquare className="w-5 h-5 text-red-500" />
                    <span>משימות שלא הושלמו</span>
                  </h3>
                  <div className="space-y-4">
                    {incompleteTasks.length > 0 ? (
                      incompleteTasks.map((task, index) => (
                        <div key={index} className="p-4 bg-red-50 rounded-xl border border-red-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <button
                              onClick={() => handleTaskStatusChange(task, true)}
                              className="w-6 h-6 bg-gray-300 rounded flex items-center justify-center text-gray-600 hover:bg-green-500 hover:text-white transition-colors"
                            >
                              +
                            </button>
                            <div className="flex-1 text-right">
                              <div className="font-medium text-gray-900">{task.title}</div>
                              <div className="text-sm text-gray-600">{task.description}</div>
                            </div>
                          </div>
                          <div className="mr-9">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              למה לא הושלמה המשימה?
                            </label>
                            <textarea
                              value={taskExplanations[task.id] || ''}
                              onChange={(e) => setTaskExplanations(prev => ({
                                ...prev,
                                [task.id]: e.target.value
                              }))}
                              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                              rows="2"
                              placeholder="הסבר למה המשימה לא הושלמה..."
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-4">
                        אין משימות שלא הושלמו
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* שאלות הסיכום */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                שאלות סיכום {getSummaryTypeLabel()}
              </h3>
              
              <div className="space-y-6">
                {/* הרגשה כללית */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    איך הייתה ההרגשה הכללית שלך ב{getSummaryTypeLabel()}?
                  </label>
                  <div className="flex justify-center items-center gap-4">
                    {[1, 2, 3, 4, 5].map(value => (
                      <button
                        key={value}
                        onClick={() => setGeneralFeeling(value)}
                        className={`p-4 rounded-xl transition-all ${
                          generalFeeling === value 
                            ? 'bg-blue-500 text-white scale-110 shadow-lg' 
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        <div className="text-2xl">{getFeelingEmoji(value)}</div>
                        <div className="text-xs mt-1">{value}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* השגת יעדים */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    האם עמדת ביעדים שהצבת לעצמך?
                  </label>
                  <textarea
                    value={summary.goalsAchieved}
                    onChange={(e) => setSummary(prev => ({ ...prev, goalsAchieved: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="פרט על היעדים שהשגת ואלו שלא..."
                  />
                </div>

                {/* ביצוע משימות */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    האם כל המשימות בוצעו כמו שצריך?
                  </label>
                  <textarea
                    value={summary.tasksCompleted}
                    onChange={(e) => setSummary(prev => ({ ...prev, tasksCompleted: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="תאר את רמת הביצוע של המשימות..."
                  />
                </div>

                {/* מכשולים */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    האם היו מכשולים לא צפויים בדרך?
                  </label>
                  <textarea
                    value={summary.unexpectedObstacles}
                    onChange={(e) => setSummary(prev => ({ ...prev, unexpectedObstacles: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="תאר מכשולים שנתקלת בהם..."
                  />
                </div>

                {/* התמודדות עם מכשולים */}
                {summary.unexpectedObstacles && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      איך התמודדת עם המכשולים?
                    </label>
                    <textarea
                      value={summary.obstacleHandling}
                      onChange={(e) => setSummary(prev => ({ ...prev, obstacleHandling: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                      placeholder="תאר את דרכי ההתמודדות..."
                    />
                  </div>
                )}

                {/* שיפור ושימור */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">שיפור ושימור</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        מה עשית כמו שצריך ותרצה לשמר?
                      </label>
                      <textarea
                        value={summary.whatWorkedWell}
                        onChange={(e) => setSummary(prev => ({ ...prev, whatWorkedWell: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="דברים שעבדו טוב..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        מה למדת מ{getSummaryTypeLabel()} הז{summaryType === 'year' ? 'את' : 'ה'}?
                      </label>
                      <textarea
                        value={summary.lessonsLearned}
                        onChange={(e) => setSummary(prev => ({ ...prev, lessonsLearned: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="תובנות ולקחים..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        איזה דברים אתה מרגיש שצריך לשפר?
                      </label>
                      <textarea
                        value={summary.areasToImprove}
                        onChange={(e) => setSummary(prev => ({ ...prev, areasToImprove: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="תחומים לשיפור..."
                      />
                    </div>
                  </div>
                </div>

                {/* שאלות נוספות */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">תובנות נוספות</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        נקודות השיא של {getSummaryTypeLabel()}
                      </label>
                      <textarea
                        value={summary.keyHighlights}
                        onChange={(e) => setSummary(prev => ({ ...prev, keyHighlights: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="רגעים משמעותיים, הצלחות מיוחדות..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        איך היה שיתוף הפעולה עם הצוות?
                      </label>
                      <textarea
                        value={summary.teamCollaboration}
                        onChange={(e) => setSummary(prev => ({ ...prev, teamCollaboration: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="עבודת צוות, תקשורת, שיתוף פעולה..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        התפתחות אישית ומקצועית
                      </label>
                      <textarea
                        value={summary.personalGrowth}
                        onChange={(e) => setSummary(prev => ({ ...prev, personalGrowth: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="כישורים חדשים, התקדמות מקצועית..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        יעדים ל{getSummaryTypeLabel()} הבא{summaryType === 'year' ? 'ה' : ''}
                      </label>
                      <textarea
                        value={summary.nextPeriodGoals}
                        onChange={(e) => setSummary(prev => ({ ...prev, nextPeriodGoals: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="מה תרצה להשיג בתקופה הבאה..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* כפתורי פעולה */}
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setSummaryType('')}
                className="px-8 py-4 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-3 text-lg font-semibold"
              >
                <ArrowLeft className="w-6 h-6" />
                <span>חזור</span>
              </button>
              
              <button
                onClick={handleSave}
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl hover:from-green-600 hover:to-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-3 text-lg font-semibold"
              >
                <Save className="w-6 h-6" />
                <span>שמור סיכום</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DaySummaryForm;